import * as React from "react";
import * as LocaleCurrency from "locale-currency";
// import * as lcid from "lcid";
import * as lcid from "../../../../utilities/lcid/lcid";
import { TextField } from "@fluentui/react/lib/TextField";

import { InputProps } from "../../interfaces/props/InputProps";
import { NumberHelper } from "../helpers/numberParsor";
import { formatParts } from "../../config/constants";
import { localStringToNumber } from "../helpers/stringHelper";
import getSymbolFromCurrency = require("currency-symbol-map");
import { logger } from "../../../../utilities/logger";
import { inputResult } from "../helpers/inputResult";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

export const Currency: React.FunctionComponent<InputProps> = ({
  displayName,
  isRequired,
  inputValue,
  inputValidator,
  updateInputValidationStatus,
  error,
  regionalSetting
}: InputProps) => {
  const [currencySymbol, setCurrencySymbol] = React.useState<string>();
  const [currencyFormatter, setCurrencyFormat] = React.useState<Intl.NumberFormat>(null);
  const [formattedCurrencyValue, setFormattedCurrencyValue] = React.useState<string>(null);
  const [isInFocus, setIsInFocus] = React.useState<boolean>(false);
  const [numericInputError, setNumericInputError] = React.useState<string>();

  React.useEffect(() => {
    const lcidFormat = lcid.from(regionalSetting?.CollationLCID).replace("_", "-");
    const currency = LocaleCurrency.getCurrency(lcidFormat);
    const currencyFormatter = new Intl.NumberFormat(lcidFormat, {
      style: formatParts.currency,
      currency: currency
    });

    setCurrencyFormat(currencyFormatter);
    const currencySymbol = getSymbolFromCurrency(currency);
    setCurrencySymbol(currencySymbol);
  }, []);

  React.useEffect(() => {
    if (currencyFormatter && !isNaN(Number(inputValue?.value ?? undefined))) {
      setFormattedCurrencyValue(currencyFormatter.format(inputValue.value));
    } else {
      setFormattedCurrencyValue(null);
    }
  }, [currencyFormatter, inputValue?.value]);

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "currency.tsx (useEffect)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const onChangeNumberField = React.useCallback((event: any, value?: string) => {
    // updateInputValidationStatus(inputValidator.validate(NumberHelper.realParseFloat(value)));
    const validationResult: inputResult = inputValidator.validate(value);
    if (value) {
      setNumericInputError(validationResult?.error);
    }
    updateInputValidationStatus(validationResult);
  }, []);

  const onChangeTextField = React.useCallback((event: any, value?: string) => {
    return;
  }, []);

  const onBlur = (event: any): void => {
    const validationResult: inputResult = inputValidator.validate(event.target.value);
    setNumericInputError(validationResult?.error);
    updateInputValidationStatus(validationResult);
    if (event.target.value) {
      const numberValue = localStringToNumber(event.target.value);
      setFormattedCurrencyValue(currencyFormatter.format(numberValue));
    }
    else {
      setFormattedCurrencyValue(null);
    }
    setIsInFocus(false);
  };

  const onFocus = (event: any): void => {
    setIsInFocus(true);
  };

  const numberInputField = (
    <TextField
      label={`${displayName} ${currencySymbol}`}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      step=".01"
      type="number"
      value={inputValue?.value ?? ""}
      onChange={onChangeNumberField}
      onBlur={onBlur}
      required={isRequired()}
      errorMessage={numericInputError}
      className="xb-sp-input"
    />
  );

  const textInputField = (
    <TextField
      label={`${displayName} ${currencySymbol}`}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      type="text"
      value={formattedCurrencyValue ?? ""}
      onChange={onChangeTextField}
      onFocus={onFocus}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
    />
  );

  return isInFocus ? numberInputField : textInputField;
};

export default Currency;
